import {
  Alert,
  Button,
  Checkbox,
  CloseButton,
  CondensedButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconTrashLine,
  List,
  Modal,
  ScreenReaderContent,
  Spinner,
  Text,
  TextInput,
  ToggleDetails,
} from "@instructure/ui";
import { IconSearchLine } from "@instructure/ui-icons";
import { t } from "i18next";
import { useContext, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { CourseGroupService } from "../../services/CourseGroupService";
import { ProgramService } from "../../services/ProgramService";
import { validateURI } from "../../utils/URIUtils";
import { sort } from "../../utils/utils";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import {
  CourseGroupsContext,
  ProgramContext,
} from "../OrganizationalStructure/OrganizationalStructure";
import { SINGLE, Selector } from "../Selectors/Selector";
import CourseGroupModal from "./CourseGroupModal";

const ProgramsModal = ({
  open,
  setOpen,
  department,
  title,
  service,
  program,
  setProgram,
}) => {
  const showNotification = useNotification();
  const { programTypes, programLevels } = useContext(ProgramContext);
  const [courseGroupList, setCourseGroupList] = useContext(CourseGroupsContext);
  const [addCourseGroupModalOpen, setAddCourseGroupModalOpen] = useState(false);
  const [selectedCourseGroups, setSelectedCourseGroups] = useState([]);
  const [createProgramLoad, setCreateProgramLoad] = useState(false);
  const [programId, setProgramId] = useState(false);
  const [filteredCourseGroupList, setFilteredCourseGroupList] = useState([]);
  const [cgTrigger, setCgTrigger] = useState(false);
  const [errFlag, setErrFlag] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [selectedProgramLevel, setSelectedProgramLevel] = useState("");
  const [selectedProgramType, setSelectedProgramType] = useState("");
  const degreeNameRef = useRef();
  const abbreviationRef = useRef();
  const accreditorNameRef = useRef();
  const accreditorUrlRef = useRef();

  let lastSearch = undefined;
  useEffect(() => {
    if (cgTrigger) {
      CourseGroupService.getCourseGroups().then((groups) => {
        groups = sort(groups, (cg) => cg.name);
        setCourseGroupList(groups);
        setCgTrigger(false);
      });
    }
  }, [cgTrigger, setCourseGroupList]);

  useEffect(() => {
    setFilteredCourseGroupList(
      courseGroupList.filter((group) => {
        return group.name.toLowerCase().includes(searchText.toLowerCase());
      })
    );
  }, [courseGroupList, searchText]);

  useEffect(() => {
    if (program.id && open) {
      setProgramId(program.id);
      setSelectedProgramLevel(program.programLevel);
      setSelectedProgramType(program.programType);
      if (program.courseGroups && program.courseGroups.length > 0) {
        setSelectedCourseGroups(program.courseGroups);
      } else {
        ProgramService.getCourseGroupsByProgram(program.id)
          .then((cg) => {
            if (cg.course_groups.length > 0) {
              setProgram((prevData) => ({
                ...prevData,
                courseGroups: cg.course_groups,
              }));
            }
          })
          .catch(() => {
            showNotification(
              t("notifications.general.fetchError", {
                obj: t("programs.courseGroups"),
              }),
              ERROR
            );
          });
      }
    }
  }, [open, program, setProgram, showNotification]);

  const handleClose = () => {
    setSelectedProgramLevel("");
    setSelectedProgramType("");
    setSelectedCourseGroups([]);
    setCreateProgramLoad(false);
    setErrFlag(false);
  };

  const handleSave = () => {
    if (
      selectedProgramLevel &&
      selectedProgramType &&
      degreeNameRef.current.value &&
      abbreviationRef.current.value
    ) {
      setErrFlag(false);
      setCreateProgramLoad(true);
      const program = {};
      program.program_level = selectedProgramLevel;
      program.program_type = selectedProgramType;
      program.degree_name = degreeNameRef.current.value;
      program.abbreviation = abbreviationRef.current.value;
      if (accreditorUrlRef.current.value) {
        const isValid = validateURI(accreditorUrlRef.current.value);
        if (!isValid) {
          showNotification(t("programs.invalidURL"), ERROR);
          setCreateProgramLoad(false);
          return;
        }
        if (!accreditorNameRef.current.value) {
          showNotification(t("programs.associateAccreditor"), ERROR);
          setCreateProgramLoad(false);
          return;
        }
      }
      program.accreditor =
        accreditorNameRef.current.value + "-" + accreditorUrlRef.current.value;

      if (selectedCourseGroups) {
        program.course_groups = selectedCourseGroups.map((e) => e.id);
      }
      program.ac_sub = department.ddl_id;
      program.id = programId;
      service(program);
    } else {
      setErrFlag(true);
    }
  };

  const updateSelectedCourseGroups = (courseGroup, id) => {
    const index = selectedCourseGroups.map((e) => e.name).indexOf(courseGroup);

    if (index === -1) {
      setSelectedCourseGroups((prevCourseGroups) => [
        ...prevCourseGroups,
        { name: courseGroup, id: id },
      ]);
    } else {
      setSelectedCourseGroups((prevCourseGroups) => [
        ...prevCourseGroups.slice(0, index),
        ...prevCourseGroups.slice(index + 1),
      ]);
    }
  };

  const removeCourseGroup = (courseGroup) => {
    setSelectedCourseGroups((prevCourseGroups) =>
      prevCourseGroups.filter((group) => group !== courseGroup)
    );
  };

  const handleSelectedProgramLevel = (selectedProgramLevel) => {
    setSelectedProgramLevel(selectedProgramLevel);
  };

  const handleSelectedProgramType = (selectedProgramType) => {
    setSelectedProgramType(selectedProgramType);
  };

  const handleGroupToggle = (group, expanded, index) => {
    if (expanded) {
      CourseGroupService.getCoursesByGroupById(group.id).then((courses) => {
        group.courses = courses.courses;
        setCourseGroupList((prevList) => [
          ...prevList.slice(0, index),
          { ...prevList[index], courses: courses.courses },
          ...prevList.slice(index + 1),
        ]);
      });
    }
  };

  const handleChange = (_e, value) => {
    window.clearTimeout(lastSearch);
    lastSearch = window.setTimeout(() => {
      setSearchText(value);
    }, 500);
  };

  return (
    <>
      <Modal open={open} size="large" label={title} onExited={handleClose}>
        <Modal.Header>
          <Grid hAlign="space-between">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h1">
                  {title}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={() => setOpen(false)}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>
          {errFlag && (
            <Alert variant="warning" margin="0 0 medium 0">
              {t("programs.createProgramError")}
            </Alert>
          )}
          <Flex wrap="wrap" alignItems="end" gap="small">
            <Flex.Item size="100%">
              <Text size="small">{t("form.requiredLegend")}</Text>
            </Flex.Item>
            <Flex.Item size="20">
              <Selector
                id="program-level-selector"
                type={SINGLE}
                size="small"
                selectHandler={handleSelectedProgramLevel}
                options={programLevels}
                selectedItemId={selectedProgramLevel}
                label={<Text size="small">{t("programs.programLevel")}*</Text>}
              />
            </Flex.Item>
            <Flex.Item align="center" margin="medium 0 0 0">
              <Text size="small" weight="bold">
                {t("programs.of")}
              </Text>
            </Flex.Item>
            <Flex.Item shouldGrow>
              <Selector
                id="program-type-selector"
                type={SINGLE}
                size="small"
                selectHandler={handleSelectedProgramType}
                options={programTypes}
                selectedItemId={selectedProgramType}
                label={t("programType.programType") + "*"}
              />
            </Flex.Item>
            <Flex.Item align="center" margin="medium 0 0 0">
              <Text size="small" weight="bold">
                {t("programs.in")}
              </Text>
            </Flex.Item>
            <Flex.Item shouldGrow>
              <TextInput
                id="discipline-selector"
                size="small"
                renderLabel={
                  <Text size="small">{t("programs.discipline")}</Text>
                }
                defaultValue={department.name}
                disabled
              />
            </Flex.Item>
            <Flex.Item size="70%">
              <TextInput
                id="degree-name-input"
                renderLabel={
                  <Text size="small">{t("programs.degreeName")}*</Text>
                }
                size="small"
                ref={degreeNameRef}
                defaultValue={program.degreeName}
              />
            </Flex.Item>
            <Flex.Item shouldGrow>
              <TextInput
                id="abbreviation-input"
                renderLabel={
                  <Text size="small">{t("programs.abbreviation")}*</Text>
                }
                size="small"
                ref={abbreviationRef}
                defaultValue={program.abbreviation}
              />
            </Flex.Item>
            <Flex.Item shouldGrow>
              <TextInput
                id="course-group-input"
                renderLabel={
                  <Text size="small">{t("programs.filterCourseGroups")}</Text>
                }
                placeholder={t("programs.filteringCourseGroups")}
                size="small"
                onChange={handleChange}
                renderBeforeInput={IconSearchLine}
              />
            </Flex.Item>
            <Flex.Item shouldGrow>
              <TextInput
                id="accreditor-name-input"
                size="small"
                renderLabel={
                  <Text size="small">{t("programs.accreditorName")}</Text>
                }
                ref={accreditorNameRef}
                defaultValue={program.accreditor.split("-")[0]}
              />
            </Flex.Item>
            <Flex.Item shouldGrow>
              <TextInput
                id="accreditor-url-input"
                size="small"
                renderLabel={
                  <Text size="small">{t("programs.accreditorUrl")}</Text>
                }
                ref={accreditorUrlRef}
                defaultValue={program.accreditor.split("-")[1]}
              />
            </Flex.Item>
            <Flex.Item size="100%">
              <Flex alignItems="start" justifyItems="space-around">
                <Flex.Item size="50%" padding="0 x-small 0 0">
                  <Flex
                    alignItems="center"
                    justifyItems="space-between"
                    margin="0 0 small 0"
                  >
                    <Flex.Item>
                      <Text size="small" weight="bold">
                        {t("programs.selectCourseGroups")}
                      </Text>
                    </Flex.Item>
                    <Flex.Item>
                      <CondensedButton
                        padding="medium"
                        size="small"
                        renderIcon={IconAddLine}
                        onClick={() => setAddCourseGroupModalOpen(true)}
                      >
                        {t("programs.addCourseGroup")}
                      </CondensedButton>
                    </Flex.Item>
                  </Flex>

                  <Card style={{ height: 226, overflow: "auto" }}>
                    {filteredCourseGroupList.map((courseGroup, key) => (
                      <Flex key={key} padding="0 small" alignItems="center">
                        <Flex.Item>
                          <Checkbox
                            label={
                              <ScreenReaderContent>
                                {courseGroup.name}
                              </ScreenReaderContent>
                            }
                            size="small"
                            value={courseGroup.name}
                            id={courseGroup.id}
                            onChange={(e) => {
                              updateSelectedCourseGroups(
                                e.target.value,
                                e.target.id
                              );
                            }}
                            checked={
                              selectedCourseGroups
                                .map((e) => e.id)
                                .indexOf(courseGroup.id) !== -1
                            }
                          />
                        </Flex.Item>
                        <Flex.Item shouldGrow>
                          <ToggleDetails
                            summary={courseGroup.name}
                            size="small"
                            onToggle={(_e, expanded) =>
                              handleGroupToggle(courseGroup, expanded, key)
                            }
                          >
                            {courseGroup.courses ? (
                              <List isUnstyled>
                                {courseGroup.courses.map((course, key) => (
                                  <List.Item key={key}>
                                    <Text size="small">{`${course.code}`}</Text>
                                  </List.Item>
                                ))}
                              </List>
                            ) : (
                              <Skeleton />
                            )}
                          </ToggleDetails>
                        </Flex.Item>
                      </Flex>
                    ))}
                  </Card>
                </Flex.Item>
                <Flex.Item shouldGrow padding="0 0 0 x-small">
                  <Flex
                    alignItems="center"
                    justifyItems="space-between"
                    margin="0 0 small 0"
                  >
                    <Flex.Item>
                      <Text size="small" weight="bold">
                        {t("programs.selectedCourseGroups")}
                      </Text>
                    </Flex.Item>
                  </Flex>

                  <Card style={{ height: 226, overflow: "auto" }}>
                    <List isUnstyled>
                      {selectedCourseGroups.map((courseGroup, key) => (
                        <List.Item key={key} margin="0 0 small 0">
                          <Flex justifyItems="space-between">
                            <Flex.Item>
                              <Text size="small">{courseGroup.name}</Text>
                            </Flex.Item>
                            <Flex.Item>
                              <CondensedButton
                                margin="0 0 xxx-small 0"
                                onClick={() => removeCourseGroup(courseGroup)}
                              >
                                <ScreenReaderContent>
                                  {t("global.remove") + " " + courseGroup.name}
                                </ScreenReaderContent>
                                <IconTrashLine />
                              </CondensedButton>
                            </Flex.Item>
                          </Flex>
                        </List.Item>
                      ))}
                    </List>
                  </Card>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small" onClick={() => setOpen(false)}>
            {t("global.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={createProgramLoad}
          >
            {createProgramLoad ? (
              <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
            ) : (
              <Text>{t("global.save")}</Text>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <CourseGroupModal
        open={addCourseGroupModalOpen}
        setOpen={setAddCourseGroupModalOpen}
        title={t("programs.addCourseGroup")}
        setTrigger={setCgTrigger}
      />
    </>
  );
};

export default ProgramsModal;
